import styled from "@emotion/styled";
import {
  AbsoluteLink,
  Avatar,
  Box,
  Button,
  FlexRow,
  IconArrow,
  RatingIcon,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import { paths } from "utils/paths";
import type { ArtistReview, ProjectReview } from "../types";

type ArtistReviewCardProps = {
  review: ArtistReview | ProjectReview;
};

function ArtistReviewCard({ review }: ArtistReviewCardProps) {
  const reviewerName =
    review.isAnonymous || review.displayName === undefined
      ? "Anonymous"
      : review.displayName;

  return (
    <Review as="li" key={review.reviewId}>
      {/* TODO: link to review */}
      <AbsoluteLink to={paths.artistReviews(review.reviewableId)}>
        <ScreenReaderText>
          Read {reviewerName}&apos;s review of {review.displayProjectName}
        </ScreenReaderText>
      </AbsoluteLink>
      <FlexRow flexDirection="column" alignItems="stretch" gap=".5em">
        <ReviewAuthor>
          <ReviewAvatar>
            <SAvatar
              src={review.isAnonymous ? undefined : review.displayAvatar}
              alt={
                review.isAnonymous
                  ? ""
                  : `Profile picture for ${review.displayName === undefined}`
              }
              loading="lazy"
            />
            <ReviewRatingIcon>
              <RatingIcon rating={review.rating} aria-label="hidden" />
            </ReviewRatingIcon>
          </ReviewAvatar>
          <Stack gap="0" as="h3">
            <ReviewUsername as="span" size="sm">
              {reviewerName}
            </ReviewUsername>
            <ReviewRatingText as="span" size="xxxs">
              Gave <em>{review.rating}</em> to {review.displayProjectName}
            </ReviewRatingText>
          </Stack>
        </ReviewAuthor>

        <Divider />

        <ReviewContent>
          <ReviewForYouIf>{review.forYouIf}</ReviewForYouIf>
          <Button as="span" size="xxs" variant="blank" aria-hidden>
            Read Review <IconArrow />
          </Button>
        </ReviewContent>
      </FlexRow>
    </Review>
  );
}

const Divider = styled.div(({ theme }) => ({
  background: theme.colors.fg20,
  height: 1,
  marginTop: ".66rem",
  width: "100%",
}));

const Review = styled(Box)(({ theme }) => ({
  borderRadius: theme.borderRadius.md,
  display: "flex",
  justifyContent: "stretch",
  lineHeight: 1.25,
  overflow: "hidden",
  position: "relative",
  width: "100%",
  "&>*": {
    flex: 1,
  },
  "a::after": {
    content: "''",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
}));

const ReviewContent = styled.div({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: 6,
  p: {
    marginBottom: 0,
  },
  span: {
    justifyContent: "flex-start",
  },
});

const lineHeight = 16;
const ReviewForYouIf = styled(Text)({
  flex: 1,
  fontSize: 13,
  lineHeight: `${lineHeight}px`,
  height: `${lineHeight * 3}px`,
  maxHeight: `${lineHeight * 3}px`,
  minHeight: lineHeight * 2,
  overflow: "hidden",
  position: "relative",
  "&::after": {
    content: "''",
    position: "absolute",
    right: 0,
    bottom: 0,
    height: `${lineHeight}px`,
    width: "66%",
    zIndex: 2,
    backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0) 0%, rgb(255 255 255) 90% )`,
    pointerEvents: "none",
  },
});
const ReviewAvatar = styled.div({
  gridArea: "avatar",
  width: 46,
  height: 46,
  position: "relative",
});

const SAvatar = styled(Avatar)({
  gridArea: "avatar",
  width: 46,
  height: 46,
});

const ReviewUsername = styled(Text)(({ theme }) => ({
  ...theme.fonts.display,
  color: theme.colors.fg,
  lineHeight: 1.1,
  gridArea: "username",
  marginBottom: 0,
  transform: "translateY(8px)",
}));

const ReviewRatingIcon = styled.div(({ theme }) => ({
  alignSelf: "start",
  gridArea: "avatar",
  position: "absolute",
  right: "-.5rem",
  bottom: "-.2rem",
  overflow: "hidden",
  justifySelf: "end",
  display: "flex",
  width: 30,
  height: 30,
  svg: {
    height: 30,
    position: "relative",
    width: 30,
    zIndex: 2,
  },
  "&::before": {
    backgroundColor: theme.colors.bg,
    borderRadius: 999,
    boxShadow: theme.boxShadow.light,
    content: "''",
    display: "block",
    inset: 2,
    position: "absolute",
    zIndex: 1,
  },
}));

const ReviewRatingText = styled(Text)(({ theme }) => ({
  ...theme.fonts.body,
  fontWeight: "normal",
  alignSelf: "center",
  gridArea: "rating",
  display: "block",
  color: theme.colors.fg80,
  "&,*": {
    margin: 0,
  },
  em: {
    whiteSpace: "nowrap",
  },
}));

const ReviewAuthor = styled.div({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gridTemplateAreas: "'avatar name'",
  gap: "4px 1rem",
  marginBottom: -5,
  h3: {
    margin: "0 auto 0 0",
  },
});

export { ArtistReviewCard };
